import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Crypto from 'expo-crypto';
import { Platform } from 'react-native';

export type PartnerDiagnosticPhase =
  | 'resolve'
  | 'save'
  | 'upload_prepare'
  | 'upload_transfer'
  | 'upload_confirm'
  | 'complete'
  | 'client_init';

export type PartnerDiagnosticCode =
  | 'network_error'
  | 'request_failed'
  | 'link_inactive'
  | 'forbidden'
  | 'conflict'
  | 'media_invalid'
  | 'required_pending'
  | 'validation_error'
  | 'unexpected_error';

export type PartnerPendingDiagnostic = {
  requestId: string;
  phase: PartnerDiagnosticPhase;
  code: PartnerDiagnosticCode;
  online: boolean;
  attempt: number;
  build?: string;
};

export type PartnerPendingMedia = {
  blob: Blob;
  mimeType: string;
  prepareRequestId: string;
  confirmRequestId: string;
  uploadId?: string;
  needsRebase?: boolean;
  prepareExpectedRevision: number;
  confirmExpectedRevision: number | null;
  stage: 'prepare' | 'upload' | 'confirm';
};

export type PartnerChecklistDraft = {
  key: string;
  expiresAt: string;
  values: Record<string, string | null>;
  observations: Record<string, string | null>;
  pendingChanges: Record<string, string | null>;
  pendingObservations: Record<string, string | null>;
  pendingMedia: Record<string, PartnerPendingMedia>;
  patchInFlight: {
    requestId: string;
    expectedRevision: number;
    changes: Record<string, string | null>;
    observations: Record<string, string | null>;
  } | null;
  completionRequestId: string | null;
  completionExpectedRevision: number | null;
  completionMode: 'submit' | 'complete' | null;
  terminalChecklistStatus: 'in_progress' | 'done' | null;
  terminalRevision: number | null;
  diagnostic: PartnerPendingDiagnostic | null;
  updatedAt: number;
};

const DB_NAME = 'app-guincho-partner-checklist-v1';
const STORE_NAME = 'drafts';
const NATIVE_PREFIX = '@app-guincho/partner-checklist-draft/';

const openDraftDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('O armazenamento local não está disponível.'));
    return;
  }
  const request = indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      database.createObjectStore(STORE_NAME, { keyPath: 'key' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('Não foi possível abrir o rascunho.'));
});

const indexedDbRequest = <T>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('Falha no armazenamento local.'));
});

const blobToDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result ?? ''));
  reader.onerror = () => reject(reader.error ?? new Error('Não foi possível manter a mídia pendente.'));
  reader.readAsDataURL(blob);
});

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

type NativeDraft = Omit<PartnerChecklistDraft, 'pendingMedia'> & {
  pendingMedia: Record<string, Omit<PartnerPendingMedia, 'blob'> & { blobDataUrl: string }>;
};

const serializeNativeDraft = async (draft: PartnerChecklistDraft): Promise<string> => {
  const pendingMedia: NativeDraft['pendingMedia'] = {};
  for (const [fieldId, media] of Object.entries(draft.pendingMedia)) {
    pendingMedia[fieldId] = {
      ...media,
      blobDataUrl: await blobToDataUrl(media.blob),
    };
  }
  const nativeDraft: NativeDraft = { ...draft, pendingMedia };
  return JSON.stringify(nativeDraft);
};

const hydrateNativeDraft = async (raw: string): Promise<PartnerChecklistDraft> => {
  const parsed = JSON.parse(raw) as NativeDraft;
  const pendingMedia: PartnerChecklistDraft['pendingMedia'] = {};
  for (const [fieldId, media] of Object.entries(parsed.pendingMedia ?? {})) {
    const { blobDataUrl, ...metadata } = media;
    pendingMedia[fieldId] = { ...metadata, blob: await dataUrlToBlob(blobDataUrl) };
  }
  return { ...parsed, pendingMedia };
};

export const createPartnerDraftKey = async (token: string): Promise<string> => {
  const digest = await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    `partner-checklist\u0000${token}`
  );
  return digest.toLowerCase();
};

export const makeEmptyPartnerDraft = (key: string, expiresAt: string): PartnerChecklistDraft => ({
  key,
  expiresAt,
  values: {},
  observations: {},
  pendingChanges: {},
  pendingObservations: {},
  pendingMedia: {},
  patchInFlight: null,
  completionRequestId: null,
  completionExpectedRevision: null,
  completionMode: null,
  terminalChecklistStatus: null,
  terminalRevision: null,
  diagnostic: null,
  updatedAt: Date.now(),
});

export const isPartnerDraftExpired = (draft: PartnerChecklistDraft, nowMs = Date.now()): boolean => {
  const expiresAt = Date.parse(draft.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= nowMs;
};

export const loadPartnerDraft = async (key: string): Promise<PartnerChecklistDraft | null> => {
  if (Platform.OS !== 'web') {
    const raw = await AsyncStorage.getItem(`${NATIVE_PREFIX}${key}`);
    return raw ? hydrateNativeDraft(raw) : null;
  }
  const database = await openDraftDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const result = await indexedDbRequest(transaction.objectStore(STORE_NAME).get(key));
    return (result as PartnerChecklistDraft | undefined) ?? null;
  } finally {
    database.close();
  }
};

export const savePartnerDraft = async (draft: PartnerChecklistDraft): Promise<void> => {
  const next = { ...draft, updatedAt: Date.now() };
  if (Platform.OS !== 'web') {
    await AsyncStorage.setItem(`${NATIVE_PREFIX}${next.key}`, await serializeNativeDraft(next));
    return;
  }
  const database = await openDraftDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    await indexedDbRequest(transaction.objectStore(STORE_NAME).put(next));
  } finally {
    database.close();
  }
};

export const deletePartnerDraft = async (key: string): Promise<void> => {
  if (Platform.OS !== 'web') {
    await AsyncStorage.removeItem(`${NATIVE_PREFIX}${key}`);
    return;
  }
  const database = await openDraftDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    await indexedDbRequest(transaction.objectStore(STORE_NAME).delete(key));
  } finally {
    database.close();
  }
};

export const prunePartnerDraft = (
  draft: PartnerChecklistDraft,
  allowedEditableFieldIds: Set<string>,
  visibleFieldIds: Set<string>
): PartnerChecklistDraft => {
  const allowed = new Set([...allowedEditableFieldIds].filter((fieldId) => visibleFieldIds.has(fieldId)));
  const values = Object.fromEntries(Object.entries(draft.values).filter(([fieldId]) => visibleFieldIds.has(fieldId)));
  const observations = Object.fromEntries(Object.entries(draft.observations).filter(([fieldId]) => visibleFieldIds.has(fieldId)));
  const pendingChanges = Object.fromEntries(
    Object.entries(draft.pendingChanges).filter(([fieldId]) => allowed.has(fieldId))
  );
  const pendingObservations = Object.fromEntries(
    Object.entries(draft.pendingObservations).filter(([fieldId]) => allowed.has(fieldId))
  );
  const pendingMedia = Object.fromEntries(
    Object.entries(draft.pendingMedia).filter(([fieldId]) => allowed.has(fieldId))
  );
  const retainedInFlightChanges = draft.patchInFlight
    ? Object.fromEntries(Object.entries(draft.patchInFlight.changes).filter(([fieldId]) => allowed.has(fieldId)))
    : {};
  const retainedInFlightObservations = draft.patchInFlight
    ? Object.fromEntries(Object.entries(draft.patchInFlight.observations).filter(([fieldId]) => allowed.has(fieldId)))
    : {};
  const inFlightWasPruned = Boolean(draft.patchInFlight &&
    (Object.keys(retainedInFlightChanges).length !== Object.keys(draft.patchInFlight.changes).length ||
      Object.keys(retainedInFlightObservations).length !== Object.keys(draft.patchInFlight.observations).length));
  const nextPendingChanges = { ...pendingChanges };
  const nextPendingObservations = { ...pendingObservations };
  if (inFlightWasPruned) Object.assign(nextPendingChanges, retainedInFlightChanges);
  if (inFlightWasPruned) Object.assign(nextPendingObservations, retainedInFlightObservations);
  const patchInFlight = draft.patchInFlight && !inFlightWasPruned
    ? { ...draft.patchInFlight, changes: retainedInFlightChanges, observations: retainedInFlightObservations }
    : null;
  return { ...draft, values, observations, pendingChanges: nextPendingChanges, pendingObservations: nextPendingObservations, pendingMedia, patchInFlight };
};

export const hasPartnerDraftWork = (draft: PartnerChecklistDraft | null): boolean => Boolean(
  draft && (
    Object.keys(draft.pendingChanges).length > 0 ||
    Object.keys(draft.pendingObservations).length > 0 ||
    Object.keys(draft.pendingMedia).length > 0 ||
    draft.patchInFlight !== null
  )
);

export const dataUrlToPartnerBlob = async (dataUrl: string): Promise<Blob> => dataUrlToBlob(dataUrl);
